import { useEffect } from 'react'
import type { MessageKey } from './i18n/messages'
import { matchPath, Navigate, Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom'

import * as auth from './auth'
import type { Session, Whoami } from './auth'
import { Desk } from './Desk'
import { AnomaliesView } from './anomalies/AnomaliesView'
import { PhotosView } from './photos/PhotosView'
import type { InspectionListState } from './InspectionList'
import { Login } from './Login'
import { useT } from './i18n/LocaleProvider'
import { RouteGuard } from './RouteGuard'
import { Uploader } from './Uploader'
import { CartaShell } from './shells/CartaShell'
import { defaultPathFor } from './home'
import { recallReviewSession } from './reviewSessionMemory'
import { commesse } from './inspections'
import { Deliveries } from './routes/Deliveries'
import { Operations } from './routes/Operations'
import { COPERTURA, CoverageView } from './coverage/CoverageView'
import { SCHEDA, InspectionElement } from './routes/InspectionElement'
import { QUALITA, InspectionQuality } from './routes/InspectionQuality'
import { CONSEGNA, DeliveryView } from './delivery/DeliveryView'
import { RICEVUTA_EXPORT, ExportReceipt } from './routes/ExportReceipt'
import { InspectionLayout } from './routes/InspectionLayout'
import { InspectionOverview, PANORAMICA } from './routes/InspectionOverview'
import { MapView } from './map/MapView'
import { IngestMonitor } from './routes/IngestMonitor'
import { Manuale } from './aiuto/Manuale'
import { Tasti } from './aiuto/Tasti'
import { Glossary } from './routes/Glossary'
import { NotFound } from './routes/NotFound'
import { PasswordChangeRoute } from './routes/PasswordChangeRoute'
import { UploadReceipt } from './routes/UploadReceipt'
import { WorkPool } from './routes/WorkPool'
import { AuditLog } from './routes/admin/AuditLog'
import { CatalogAdmin } from './routes/admin/CatalogAdmin'
import { ModelAdmin } from './routes/admin/ModelAdmin'
import { SettingsAdmin } from './routes/admin/SettingsAdmin'
import { UserAdmin } from './routes/admin/UserAdmin'

export type Theme = 'carta' | 'cockpit'

export interface RoutePolicy {
  path: string
  theme: Theme
  capability: string | null
}

export const routePolicies: RoutePolicy[] = [
  { path: '/login', theme: 'carta', capability: null },
  { path: '/password/change', theme: 'carta', capability: null },
  // Le parole sono di tutti: nessun permesso, solo l'accesso.
  { path: '/glossario', theme: 'carta', capability: null },
  // Il manuale e' di tutti; la mappa dei tasti solo di chi decide al banco (GI-281).
  { path: '/aiuto', theme: 'carta', capability: null },
  { path: '/aiuto/tasti', theme: 'carta', capability: auth.REVIEW_QUEUE },
  { path: '/uploads/new', theme: 'carta', capability: auth.PACKAGE_UPLOAD },
  { path: '/uploads/:uploadId/receipt', theme: 'carta', capability: auth.UPLOAD_RECEIPT_READ },
  { path: '/ingest/:jobId', theme: 'carta', capability: auth.INGEST_READ },
  { path: '/work', theme: 'carta', capability: auth.INSPECTION_POOL_READ },
  { path: '/inspections/:inspectionId', theme: 'carta', capability: auth.REVIEW_QUEUE },
  // La mappa e' la prima scheda della commessa (GI-237): chi vede la coda vede la linea.
  { path: '/inspections/:inspectionId/map', theme: 'carta', capability: auth.REVIEW_QUEUE },
  // L'elenco delle anomalie e' lettura della commessa: la coda resta il banco di chi decide.
  { path: '/inspections/:inspectionId/anomalies', theme: 'carta', capability: auth.REVIEW_QUEUE },
  // Coda, candidato e ricerca restano parti della stessa commessa: il viewer
  // porta il proprio contorno scuro nel nodo dell'immagine, non nell'intera
  // shell della pagina.
  { path: '/inspections/:inspectionId/queue', theme: 'carta', capability: auth.REVIEW_QUEUE },
  { path: '/inspections/:inspectionId/candidates/:candidateId', theme: 'carta', capability: auth.REVIEW_QUEUE },
  { path: '/inspections/:inspectionId/search', theme: 'carta', capability: auth.FINDING_MANUAL },
  // La copertura e' un modulo *dentro* la commessa e non una home: l'indirizzo
  // la contiene, e senza una commessa scelta non esiste. Tema Carta perche' e'
  // materia di registri, filtri e blocchi di consegna; il visualizzatore porta
  // il proprio contorno sul solo nodo dell'immagine.
  { path: '/inspections/:inspectionId/coverage', theme: 'carta', capability: auth.FINDING_MANUAL },
  // La scheda dell'elemento e' lettura: chi vede la coda vede anche il palo.
  { path: '/inspections/:inspectionId/elements/:elementId', theme: 'carta', capability: auth.REVIEW_QUEUE },
  { path: '/inspections/:inspectionId/progress', theme: 'carta', capability: auth.QUALITY_PROGRESS_READ },
  { path: '/inspections/:inspectionId/quality', theme: 'carta', capability: auth.QUALITY_SAMPLE },
  { path: '/inspections/:inspectionId/delivery', theme: 'carta', capability: auth.EXPORT_COMPOSE },
  { path: '/exports', theme: 'carta', capability: auth.EXPORT_RECEIPT_READ },
  { path: '/exports/:exportId', theme: 'carta', capability: auth.EXPORT_RECEIPT_READ },
  { path: '/operations', theme: 'carta', capability: auth.SETTINGS_MANAGE },
  { path: '/admin/users', theme: 'carta', capability: auth.USER_MANAGE },
  { path: '/admin/model', theme: 'carta', capability: auth.MODEL_MANAGE },
  { path: '/admin/settings', theme: 'carta', capability: auth.SETTINGS_MANAGE },
  // Il catalogo ha una capacita' propria nella matrice: dargli un instradamento
  // separato invece di annidarlo nelle impostazioni e' cio' che tiene la
  // schermata coerente col permesso che la protegge.
  { path: '/admin/catalog', theme: 'carta', capability: auth.CATALOG_IMPORT },
  { path: '/admin/audit', theme: 'carta', capability: auth.AUDIT_READ },
]

const ROUTE_STATE = new Set(['status', 'sort', 'page', 'conflict'])

type WorkStatus = 'all' | 'work' | 'frozen'
type WorkSort = 'priority' | 'recent' | 'activity'

export interface WorkRouteState {
  status: WorkStatus
  sort: WorkSort
  page: number
  extras: Record<string, string>
}

function safeQueryToken(value: string): boolean {
  return /^[A-Za-z0-9._~-]+$/.test(value)
}

function routeStateQuery(searchParams: URLSearchParams): URLSearchParams {
  const kept = new URLSearchParams()
  for (const [key, value] of searchParams.entries()) {
    if (key === 'conflict' && !safeQueryToken(value)) continue
    const keepKnown = ROUTE_STATE.has(key)
    const keepSafeUnknown = safeQueryToken(key) && safeQueryToken(value)
    if (keepKnown || keepSafeUnknown) kept.set(key, value)
  }
  return kept
}

function normalizedPage(value: string | null): number {
  if (value === null) return 1
  const parsed = Number(value)
  if (!Number.isInteger(parsed) || parsed < 1) return 1
  return parsed
}

function asWorkStatus(value: string | null): WorkStatus {
  // Per difetto **tutte** (GI-279): il filtro «con lavoro» per difetto faceva
  // cominciare la giornata su una tabella vuota ogni volta che non c'erano
  // candidati nuovi — cioe' nella normalita'. I due valori vecchi restano
  // leggibili per i collegamenti gia' incollati da qualche parte.
  if (value === 'work' || value === 'in_review') return 'work'
  if (value === 'frozen') return 'frozen'
  return 'all'
}

function asWorkSort(value: string | null): WorkSort {
  if (value === 'recent' || value === 'activity') return value
  return 'priority'
}

export function parseWorkRouteState(search: string): WorkRouteState {
  const safe = routeStateQuery(new URLSearchParams(search))
  const extras: Record<string, string> = {}
  for (const [key, value] of safe.entries()) {
    if (key === 'status' || key === 'sort' || key === 'page') continue
    extras[key] = value
  }
  return {
    status: asWorkStatus(safe.get('status')),
    sort: asWorkSort(safe.get('sort')),
    page: normalizedPage(safe.get('page')),
    extras,
  }
}

export function serializeWorkRouteState(state: WorkRouteState): string {
  const query = new URLSearchParams()
  query.set('status', state.status)
  query.set('sort', state.sort)
  query.set('page', String(state.page))
  for (const key of Object.keys(state.extras).sort()) {
    const value = state.extras[key]
    if (value !== undefined && safeQueryToken(key) && safeQueryToken(value)) query.set(key, value)
  }
  return `?${query.toString()}`
}

export function parseConflictId(search: string): string | null {
  const query = routeStateQuery(new URLSearchParams(search))
  const value = query.get('conflict')
  return value !== null && safeQueryToken(value) ? value : null
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

export function parseReviewSessionId(search: string): string | null {
  // Si controlla la forma perche' questo valore arriva dall'indirizzo e finisce
  // dentro una richiesta al servizio: cio' che viene da fuori non e' nostro
  // finche' non lo si e' guardato.
  const value = routeStateQuery(new URLSearchParams(search)).get('session')
  return value !== null && UUID.test(value) ? value : null
}

export function queuePathForInspection(inspectionId: string, search: string): string {
  const params = routeStateQuery(new URLSearchParams(search))
  const suffix = params.toString()
  return `/inspections/${inspectionId}/queue${suffix === '' ? '' : `?${suffix}`}`
}

/**
 * L'identita' del candidato che si sta guardando, letta dall'indirizzo.
 *
 * Si controlla la forma perche' questo valore arriva da fuori e finisce in un
 * indirizzo che la pagina riscrive: cio' che viene da fuori non e' nostro
 * finche' non lo si e' guardato. Non e' un UUID come la sessione — le prove e i
 * fotogrammi d'archivio portano identita' piu' brevi — quindi vale la stessa
 * regola del resto dello stato di route.
 */
export function parseCandidateId(value: string | undefined): string | null {
  if (value === undefined || value === '') return null
  return safeQueryToken(value) ? value : null
}

/**
 * Il nome della schermata del banco.
 *
 * La coda e il candidato sono due indirizzi dello **stesso** banco: il guscio
 * riporta il fuoco sul titolo quando si arriva su una schermata, e riscrivere
 * l'indirizzo a ogni candidato toglierebbe la tastiera all'operatore a meta'
 * lavoro. Con un nome solo, l'arrivo e' uno.
 */
const BANCO = 'banco-candidato'

/**
 * L'indirizzo del candidato in verifica.
 *
 * **La posizione nell'array non e' un indirizzo.** La coda si rilegge a ogni
 * ritorno della rete e a ogni ripresa: l'indice tre di prima non e' lo stesso
 * candidato di dopo, e un collegamento incollato riporterebbe un collega su
 * qualcosa di gia' deciso. L'identita' invece resta quella, e se il candidato
 * non c'e' piu' il banco lo dice.
 *
 * Senza identita' non c'e' niente da nominare: si torna all'indirizzo della
 * coda, che e' il banco senza un candidato scelto.
 */
export function candidatePathForInspection(
  inspectionId: string,
  detectionId: string | null,
  search: string,
): string {
  const safe = parseCandidateId(detectionId ?? undefined)
  if (safe === null) return queuePathForInspection(inspectionId, search)
  const params = routeStateQuery(new URLSearchParams(search))
  const suffix = params.toString()
  return `/inspections/${inspectionId}/candidates/${safe}${suffix === '' ? '' : `?${suffix}`}`
}

/** L'indirizzo della scheda dell'elemento di un candidato, con lo stato del
 *  banco e il candidato d'origine: e' cio' che permette di tornare indietro. */
export function elementPathForCandidate(
  inspectionId: string,
  candidate: { detection_id: string; element_id: string | null },
  search: string,
): string {
  const params = routeStateQuery(new URLSearchParams(search))
  const safe = parseCandidateId(candidate.detection_id)
  if (safe !== null) params.set('candidate', safe)
  const suffix = params.toString()
  return `/inspections/${inspectionId}/elements/${candidate.element_id ?? ''}${suffix === '' ? '' : `?${suffix}`}`
}

export function legacyInspectionTarget(pathAndQuery: string): string | null {
  const url = new URL(pathAndQuery, 'http://gridfly.local')
  if (url.pathname !== '/') return null
  const inspectionId = url.searchParams.get('inspection')
  if (inspectionId === null || inspectionId === '') return null

  url.searchParams.delete('inspection')
  return queuePathForInspection(inspectionId, url.search)
}

export { defaultPathFor } from './home'

export function resolveReturnTo(returnTo: string | null, who: Whoami): string | null {
  if (returnTo === null || !returnTo.startsWith('/')) return null

  const candidate = new URL(returnTo, 'http://gridfly.local')
  const policy = routePolicies.find((one) =>
    matchPath({ path: one.path, end: true }, candidate.pathname) !== null,
  )
  if (!policy) return null
  if (policy.capability !== null && !who.may.includes(policy.capability)) return null

  const filtered = routeStateQuery(candidate.searchParams)
  const suffix = filtered.toString()
  return `${candidate.pathname}${suffix === '' ? '' : `?${suffix}`}`
}

/** Il titolo della scheda del navigatore, per pagina: dieci schede tutte
 *  «GridFly — verifica» non si distinguevano (GI-255). */
const TITLE_KEYS: [string, MessageKey][] = [
  ['/login', 'tab.login'],
  ['/password/change', 'tab.login'],
  ['/glossario', 'tab.glossary'],
  ['/aiuto', 'rail.help'],
  ['/aiuto/tasti', 'keys.help'],
  ['/uploads/*', 'tab.uploads'],
  ['/ingest/*', 'nav.ingest'],
  ['/work', 'tab.pool'],
  ['/inspections/:inspectionId', 'tab.job'],
  ['/inspections/:inspectionId/map', 'tab.map'],
  ['/inspections/:inspectionId/anomalies', 'tab.anomalies'],
  ['/inspections/:inspectionId/queue', 'tab.queue'],
  ['/inspections/:inspectionId/candidates/*', 'tab.queue'],
  ['/inspections/:inspectionId/search', 'tab.photos'],
  ['/inspections/:inspectionId/coverage', 'tab.coverage'],
  ['/inspections/:inspectionId/elements/*', 'tab.element'],
  ['/inspections/:inspectionId/quality', 'tab.quality'],
  ['/inspections/:inspectionId/delivery', 'tab.delivery'],
  ['/exports/*', 'tab.deliveries'],
  ['/exports', 'tab.deliveries'],
  ['/operations', 'tab.admin'],
  ['/admin/*', 'tab.admin'],
]

export function pageTitle(pathname: string, t: (key: MessageKey) => string): string {
  const found = TITLE_KEYS.find(([path]) => matchPath({ path, end: true }, pathname) !== null)
  return found === undefined ? 'GridFly' : `${t(found[1])} — GridFly`
}

function DocumentTitle(): null {
  const location = useLocation()
  const t = useT()
  useEffect(() => {
    document.title = pageTitle(location.pathname, t)
  }, [location.pathname, t])
  return null
}

export function AppRouter({
  inside,
  who,
  onLeave,
  onEntered,
}: {
  inside: boolean | null
  who: Whoami | null
  onLeave: () => void
  onEntered: (session: Session) => void
}): JSX.Element {
  const safeTo = defaultPathFor(who)
  const t = useT()
  return (
    <>
    <DocumentTitle />
    <Routes>
      <Route
        path="/"
        element={<RootRedirect safeTo={safeTo} />}
      />

      <Route
        path="/login"
        element={<LoginRoute inside={inside} who={who} onEntered={onEntered} />}
      />

      <Route
        path="/password/change"
        element={
          <RouteGuard inside={inside} who={who} capability={null} safeTo={safeTo}>
            {who === null ? <main /> : <CartaShell title={t('title.password')} who={who} onLeave={onLeave}><PasswordChangeRoute onLeave={onLeave} /></CartaShell>}
          </RouteGuard>
        }
      />

      <Route
        path="/glossario"
        element={
          <RouteGuard inside={inside} who={who} capability={null} safeTo={safeTo}>
            {who === null ? <main /> : <CartaShell title={t('title.glossary')} who={who} onLeave={onLeave}><Glossary /></CartaShell>}
          </RouteGuard>
        }
      />

      <Route
        path="/aiuto"
        element={
          <RouteGuard inside={inside} who={who} capability={null} safeTo={safeTo}>
            {who === null ? <main /> : <CartaShell title={t('rail.help')} who={who} onLeave={onLeave}><Manuale who={who} /></CartaShell>}
          </RouteGuard>
        }
      />

      <Route
        path="/aiuto/tasti"
        element={
          <RouteGuard inside={inside} who={who} capability={auth.REVIEW_QUEUE} safeTo={safeTo}>
            {who === null ? <main /> : <CartaShell title={t('rail.help')} who={who} onLeave={onLeave}><Tasti /></CartaShell>}
          </RouteGuard>
        }
      />

      <Route
        path="/uploads/new"
        element={
          <RouteGuard inside={inside} who={who} capability={auth.PACKAGE_UPLOAD} safeTo={safeTo}>
            {who === null ? <main /> : <CartaShell title={t('title.upload')} who={who} onLeave={onLeave}><Uploader /></CartaShell>}
          </RouteGuard>
        }
      />

      <Route
        path="/uploads/:uploadId/receipt"
        element={
          <RouteGuard inside={inside} who={who} capability={auth.UPLOAD_RECEIPT_READ} safeTo={safeTo}>
            {who === null ? <main /> : <CartaShell title={t('title.uploadReceipt')} who={who} onLeave={onLeave}><UploadReceipt /></CartaShell>}
          </RouteGuard>
        }
      />

      <Route
        path="/ingest/:jobId"
        element={
          <RouteGuard inside={inside} who={who} capability={auth.INGEST_READ} safeTo={safeTo}>
            {who === null ? <main /> : <CartaShell title={t('title.ingest')} who={who} onLeave={onLeave}><IngestRoute who={who} /></CartaShell>}
          </RouteGuard>
        }
      />

      <Route
        path="/work"
        element={
          <RouteGuard inside={inside} who={who} capability={auth.INSPECTION_POOL_READ} safeTo={safeTo}>
            {who === null ? (
              <main />
            ) : (
              <CartaShell
                title={t('title.pool')}
                subtitle={t('title.poolShared')}
                who={who}
                onLeave={onLeave}
              >
                <WorkRoute />
              </CartaShell>
            )}
          </RouteGuard>
        }
      />

      <Route
        path="/inspections/:inspectionId"
        element={
          <RouteGuard inside={inside} who={who} capability={auth.REVIEW_QUEUE} safeTo={safeTo}>
            {who === null ? (
              <main />
            ) : (
              <CartaShell title={t('title.commessa')} who={who} onLeave={onLeave} announce={PANORAMICA}>
                <InspectionLayout />
              </CartaShell>
            )}
          </RouteGuard>
        }
      >
        <Route index element={who === null ? <main /> : <OverviewRoute who={who} />} />
      </Route>

      <Route
        path="/inspections/:inspectionId/anomalies"
        element={
          <RouteGuard inside={inside} who={who} capability={auth.REVIEW_QUEUE} safeTo={safeTo}>
            {who === null ? <main /> : (
              <CartaShell title={t('title.anomalies')} who={who} onLeave={onLeave}>
                <InspectionAnomaliesRoute />
              </CartaShell>
            )}
          </RouteGuard>
        }
      />

      <Route
        path="/inspections/:inspectionId/map"
        element={
          <RouteGuard inside={inside} who={who} capability={auth.REVIEW_QUEUE} safeTo={safeTo}>
            {who === null ? <main /> : (
              <CartaShell title={t('title.map')} who={who} onLeave={onLeave} announce={MAPPA}>
                <InspectionMapRoute who={who} />
              </CartaShell>
            )}
          </RouteGuard>
        }
      />

      <Route
        path="/inspections/:inspectionId/queue"
        element={
          <RouteGuard inside={inside} who={who} capability={auth.REVIEW_QUEUE} safeTo={safeTo}>
            {who === null ? (
              <main />
            ) : (
              <CartaShell
                title={t('title.queue')}
                subtitle={t('title.poolShared')}
                who={who}
                onLeave={onLeave}
                announce={BANCO}
                contour="cockpit"
              >
                <InspectionDeskRoute login={who.login} />
              </CartaShell>
            )}
          </RouteGuard>
        }
      />

      <Route
        path="/inspections/:inspectionId/candidates/:candidateId"
        element={
          <RouteGuard inside={inside} who={who} capability={auth.REVIEW_QUEUE} safeTo={safeTo}>
            {who === null ? (
              <main />
            ) : (
              <CartaShell title={t('title.desk')} who={who} onLeave={onLeave} announce={BANCO} contour="cockpit">
                <InspectionDeskRoute login={who.login} />
              </CartaShell>
            )}
          </RouteGuard>
        }
      />

      <Route
        path="/inspections/:inspectionId/search"
        element={
          <RouteGuard inside={inside} who={who} capability={auth.FINDING_MANUAL} safeTo={safeTo}>
            {who === null ? (
              <main />
            ) : (
              <CartaShell title={t('title.search')} who={who} onLeave={onLeave} announce="ricerca-foto">
                <InspectionSearchRoute />
              </CartaShell>
            )}
          </RouteGuard>
        }
      />

      <Route
        path="/inspections/:inspectionId/coverage"
        element={
          <RouteGuard inside={inside} who={who} capability={auth.FINDING_MANUAL} safeTo={safeTo}>
            {who === null ? (
              <main />
            ) : (
              <CartaShell
                title={t('title.coverage')}
                who={who}
                onLeave={onLeave}
                announce={COPERTURA}
              >
                <InspectionCoverageRoute />
              </CartaShell>
            )}
          </RouteGuard>
        }
      />

      <Route
        path="/inspections/:inspectionId/elements/:elementId"
        element={
          <RouteGuard inside={inside} who={who} capability={auth.REVIEW_QUEUE} safeTo={safeTo}>
            {who === null ? (
              <main />
            ) : (
              <CartaShell title={t('title.element')} who={who} onLeave={onLeave} announce={SCHEDA}>
                <InspectionElementRoute />
              </CartaShell>
            )}
          </RouteGuard>
        }
      />

      <Route
        path="/inspections/:inspectionId/progress"
        // L'avanzamento QA era una riga di conti su una pagina sua: ora e' la
        // carta «prossimo passo» del passaporto (S17, L-2). L'indirizzo resta
        // per i collegamenti vecchi e porta la'.
        element={<ProgressRedirect />}
      />

      <Route
        path="/inspections/:inspectionId/quality"
        element={
          <RouteGuard inside={inside} who={who} capability={auth.QUALITY_SAMPLE} safeTo={safeTo}>
            {who === null ? <main /> : (
              <CartaShell title={t('title.quality')} who={who} onLeave={onLeave} announce={QUALITA}>
                <InspectionQualityRoute who={who} />
              </CartaShell>
            )}
          </RouteGuard>
        }
      />

      <Route
        path="/inspections/:inspectionId/delivery"
        element={
          <RouteGuard inside={inside} who={who} capability={auth.EXPORT_COMPOSE} safeTo={safeTo}>
            {who === null ? <main /> : <CartaShell title={t('title.delivery')} who={who} onLeave={onLeave} announce={CONSEGNA}><InspectionDeliveryRoute who={who} /></CartaShell>}
          </RouteGuard>
        }
      />

      <Route
        path="/exports"
        element={
          <RouteGuard inside={inside} who={who} capability={auth.EXPORT_RECEIPT_READ} safeTo={safeTo}>
            {who === null ? <main /> : <CartaShell title={t('title.deliveries')} who={who} onLeave={onLeave}><Deliveries /></CartaShell>}
          </RouteGuard>
        }
      />

      <Route
        path="/exports/:exportId"
        element={
          <RouteGuard inside={inside} who={who} capability={auth.EXPORT_RECEIPT_READ} safeTo={safeTo}>
            {who === null ? <main /> : <CartaShell title={t('title.exportReceipt')} who={who} onLeave={onLeave} announce={RICEVUTA_EXPORT}><ExportReceipt who={who} /></CartaShell>}
          </RouteGuard>
        }
      />

      <Route
        path="/operations"
        element={
          <RouteGuard inside={inside} who={who} capability={auth.SETTINGS_MANAGE} safeTo={safeTo}>
            {who === null ? <main /> : <CartaShell title={t('title.operations')} who={who} onLeave={onLeave}><Operations /></CartaShell>}
          </RouteGuard>
        }
      />

      <Route
        path="/admin/users"
        element={
          <RouteGuard inside={inside} who={who} capability={auth.USER_MANAGE} safeTo={safeTo}>
            {who === null ? <main /> : <CartaShell title={t('title.users')} who={who} onLeave={onLeave}><UserAdmin /></CartaShell>}
          </RouteGuard>
        }
      />

      <Route
        path="/admin/model"
        element={
          <RouteGuard inside={inside} who={who} capability={auth.MODEL_MANAGE} safeTo={safeTo}>
            {who === null ? <main /> : <CartaShell title={t('title.model')} who={who} onLeave={onLeave}><ModelAdmin /></CartaShell>}
          </RouteGuard>
        }
      />

      <Route
        path="/admin/settings"
        element={
          <RouteGuard inside={inside} who={who} capability={auth.SETTINGS_MANAGE} safeTo={safeTo}>
            {who === null ? <main /> : <CartaShell title={t('title.settings')} who={who} onLeave={onLeave}><SettingsAdmin /></CartaShell>}
          </RouteGuard>
        }
      />

      <Route
        path="/admin/catalog"
        element={
          <RouteGuard inside={inside} who={who} capability={auth.CATALOG_IMPORT} safeTo={safeTo}>
            {who === null ? <main /> : <CartaShell title={t('title.catalog')} who={who} onLeave={onLeave}><CatalogAdmin /></CartaShell>}
          </RouteGuard>
        }
      />

      <Route
        path="/admin/audit"
        element={
          <RouteGuard inside={inside} who={who} capability={auth.AUDIT_READ} safeTo={safeTo}>
            {who === null ? <main /> : <CartaShell title={t('title.audit')} who={who} onLeave={onLeave}><AuditLog /></CartaShell>}
          </RouteGuard>
        }
      />

      <Route path="*" element={<NotFound />} />
    </Routes>
    </>
  )
}

function RootRedirect({ safeTo }: { safeTo: string | null }): JSX.Element {
  const location = useLocation()
  const target = legacyInspectionTarget(`${location.pathname}${location.search}`)
  if (target !== null) return <Navigate to={target} replace />
  return <Navigate to={safeTo ?? '/login'} replace />
}

function LoginRoute({
  inside,
  who,
  onEntered,
}: {
  inside: boolean | null
  who: Whoami | null
  onEntered: (session: Session) => void
}): JSX.Element {
  const location = useLocation()
  const t = useT()
  if (inside === null) {
    return (
      <main>
        <p role="status">{t('guard.opening')}</p>
      </main>
    )
  }
  if (inside && who !== null) {
    const requested = new URLSearchParams(location.search).get('returnTo')
    const resolved = resolveReturnTo(requested, who) ?? defaultPathFor(who)
    if (resolved === null) {
      return (
        <main>
          <h1>{t('guard.denied')}</h1>
          <p>{t('guard.noSafe')}</p>
        </main>
      )
    }
    return <Navigate to={resolved} replace />
  }
  return <Login onEntered={onEntered} />
}

function WorkRoute(): JSX.Element {
  const navigate = useNavigate()
  const location = useLocation()
  const state = parseWorkRouteState(location.search)
  const serialized = serializeWorkRouteState(state)

  useEffect(() => {
    if (location.search === serialized) return
    navigate({ pathname: '/work', search: serialized }, { replace: true })
  }, [location.search, navigate, serialized])

  function rewrite(next: WorkRouteState): void {
    navigate(
      { pathname: '/work', search: serializeWorkRouteState(next) },
      { replace: false },
    )
  }

  function onListStateChange(next: InspectionListState): void {
    rewrite({ ...state, ...next })
  }

  function onEnterQueue(inspectionId: string, reviewSessionId: string): void {
    // La sessione viaggia nell'indirizzo insieme al resto dello stato: la coda
    // deve poter dire al servizio per conto di chi sta chiedendo, e un
    // ricaricamento della pagina non deve perdere i candidati gia' riservati.
    const query = new URLSearchParams(location.search)
    query.set('session', reviewSessionId)
    navigate(queuePathForInspection(inspectionId, `?${query.toString()}`))
  }

  function onOpenJob(inspectionId: string): void {
    navigate(`/inspections/${inspectionId}`)
  }

  return (
    <WorkPool
      state={{ status: state.status, sort: state.sort, page: state.page }}
      onStateChange={onListStateChange}
      onEnterQueue={onEnterQueue}
      onOpenJob={onOpenJob}
    />
  )
}

const MAPPA = 'modulo-mappa'

function InspectionAnomaliesRoute(): JSX.Element {
  const inspectionId = useParams().inspectionId ?? ''
  return <AnomaliesView inspectionId={inspectionId} />
}

function InspectionMapRoute({ who }: { who: Whoami }): JSX.Element {
  const inspectionId = useParams().inspectionId ?? ''
  return <MapView inspectionId={inspectionId} who={who} />
}

function OverviewRoute({ who }: { who: Whoami }): JSX.Element {
  const inspectionId = useParams().inspectionId ?? ''
  return <InspectionOverview inspectionId={inspectionId} who={who} />
}

function InspectionDeskRoute({ login }: { login: string }): JSX.Element {
  const params = useParams()
  const inspectionId = params.inspectionId ?? ''
  const location = useLocation()
  const navigate = useNavigate()
  const fromAddress = parseReviewSessionId(location.search)
  // La sessione ricordata per la commessa (GI-279): chi arriva sul banco da una
  // scheda, da un collegamento della mappa o dal tasto «indietro» ritrova la
  // sessione che aveva, invece della sola osservazione. Nell'indirizzo, e non
  // solo in memoria, perche' e' l'indirizzo che si copia e si ricarica.
  const remembered = fromAddress === null ? recallReviewSession(inspectionId) : null
  useEffect(() => {
    if (remembered === null) return
    const query = new URLSearchParams(location.search)
    query.set('session', remembered)
    navigate(`${location.pathname}?${query.toString()}`, { replace: true })
  }, [remembered, location.pathname, location.search, navigate])
  if (remembered !== null) return <main />
  return (
    <Desk
      inspectionId={inspectionId}
      login={login}
      conflictId={parseConflictId(location.search)}
      // La sessione aperta nel pool: e' con quella che i candidati sono stati
      // riservati, e senza il banco chiederebbe la coda come se non avesse
      // riservato niente.
      reviewSessionId={parseReviewSessionId(location.search)}
      // Il candidato in verifica sta nell'indirizzo per identita': un
      // ricaricamento, un collegamento incollato e il tasto «indietro» tornano
      // sullo stesso candidato, non sulla stessa posizione di un elenco che nel
      // frattempo si e' rileto.
      candidateId={parseCandidateId(params.candidateId)}
      onCandidate={(detectionId) =>
        navigate(candidatePathForInspection(inspectionId, detectionId, location.search), {
          // Sostituire e non impilare: sei candidati decisi lascerebbero sei
          // tappe nella cronologia, e il tasto «indietro» risalirebbe il
          // batch un verdetto per volta invece di uscire dal banco.
          replace: true,
        })
      }
      // L'uscita di «G». Uno spostamento dentro la pagina e non un
      // collegamento: un caricamento intero perderebbe il fotogramma gia'
      // scaricato e riaprirebbe la sessione per niente.
      // L'uscita di «G»: le Foto aperte sul fotogramma che si aveva davanti,
      // cercato per nome (e' il filtro che la pagina Foto porta
      // nell'indirizzo). Senza fotogramma, la pagina intera.
      onManualFinding={(frame) =>
        navigate(
          frame?.filename
            ? `/inspections/${inspectionId}/search?q=${encodeURIComponent(frame.filename)}`
            : `/inspections/${inspectionId}/search`,
        )
      }
      // L'uscita di «O»: la scheda porta con se' il candidato da cui si e'
      // partiti e lo stato dell'indirizzo, cosi' il ritorno trova la stessa
      // sessione e lo stesso candidato.
      onElement={(candidate) => navigate(elementPathForCandidate(inspectionId, candidate, location.search))}
      // La sessione aperta sul posto entra nell'indirizzo come quella del
      // pool: stesso parametro, stessa pagina, senza una tappa in piu' nella
      // cronologia — il tasto «indietro» deve uscire dal banco, non tornare
      // alla sua versione in sola osservazione.
      onSession={(reviewSessionId) => {
        const query = new URLSearchParams(location.search)
        query.set('session', reviewSessionId)
        navigate(`${location.pathname}?${query.toString()}`, { replace: true })
      }}
      // Gli altri punti di vista: il fotogrammario filtrato sull'elemento del
      // candidato, la stessa uscita che ha la mappa (GI-248).
      onElementFrames={(candidate) =>
        navigate(`/inspections/${inspectionId}/search?element=${encodeURIComponent(candidate.element_id ?? '')}`)
      }
    />
  )
}

/**
 * Il monitor ingest con la sua commessa nell'indirizzo (GI-279).
 *
 * Aperto dalla ricevuta di caricamento, il monitor arrivava senza `?inspection`
 * e la shell non aveva ne' schede ne' percorso: un vicolo cieco. Chi vede il
 * pool puo' ritrovare la commessa dal job e riscrivere l'indirizzo; chi non lo
 * vede — chi carica — resta con la sola pagina, ma con il collegamento alla
 * ricevuta da cui e' venuto.
 */
function IngestRoute({ who }: { who: Whoami }): JSX.Element {
  const jobId = useParams().jobId ?? ''
  const location = useLocation()
  const navigate = useNavigate()
  const missing = new URLSearchParams(location.search).get('inspection') === null
  const canLook = who.may.includes(auth.INSPECTION_POOL_READ)
  useEffect(() => {
    if (!missing || !canLook || jobId === '') return
    let cancelled = false
    void commesse()
      .then((rows) => {
        if (cancelled) return
        const found = rows.find((one) => one.job_id === jobId)
        if (found !== undefined) {
          navigate(`/ingest/${jobId}?inspection=${encodeURIComponent(found.inspection_id)}`, { replace: true })
        }
      })
      .catch(() => undefined)
    return () => {
      cancelled = true
    }
  }, [missing, canLook, jobId, navigate])
  return <IngestMonitor />
}

function InspectionElementRoute(): JSX.Element {
  const params = useParams()
  return (
    <InspectionElement
      inspectionId={params.inspectionId ?? ''}
      elementId={params.elementId ?? ''}
    />
  )
}

function InspectionCoverageRoute(): JSX.Element {
  const inspectionId = useParams().inspectionId ?? ''
  return <CoverageView inspectionId={inspectionId} />
}

function InspectionSearchRoute(): JSX.Element {
  const inspectionId = useParams().inspectionId ?? ''
  return <PhotosView inspectionId={inspectionId} />
}


function InspectionQualityRoute({ who }: { who: Whoami }): JSX.Element {
  const inspectionId = useParams().inspectionId ?? ''
  return <InspectionQuality inspectionId={inspectionId} who={who} />
}

function InspectionDeliveryRoute({ who }: { who: Whoami }): JSX.Element {
  const inspectionId = useParams().inspectionId ?? ''
  return <DeliveryView inspectionId={inspectionId} who={who} />
}

function ProgressRedirect(): JSX.Element {
  const params = useParams()
  return <Navigate to={`/inspections/${params.inspectionId ?? ''}`} replace />
}
